import { Box, Icon, Menu, Text } from '@zillow/constellation-10';
import {
  IconChevronDownOutline,
  IconExternalOutline,
  IconLocationOutline,
  IconMinusOutline,
  IconSparkleFilled,
  IconSparkleOutline,
} from '@zillow/constellation-icons';

import { PlacementSource, SmartConfigMode } from '@zg-rentals/ts-rental-manager-api-client';

import {
  chipLabelRecipe,
  menuActionIconRecipe,
  menuActionLabelRecipe,
  selectedOptionRow,
} from './SmartAssignControlCell.styles';
import { chipLabelStyle, chipRecipe } from './SpotlightControlCell.styles';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';
import { isManagedByFeedReadOnly, isRemovableManagedByFeedSpotlight } from './utils/cacheHelpers';

import type { ComponentType, MouseEvent } from 'react';

const swallow = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

type StateKey = 'pinned' | 'smartAssign' | 'excluded';

type Action = {
  critical?: boolean;
  selected?: boolean;
  desc: string;
  icon: ComponentType;
  label: string;
  onSelect: () => void;
};

// Inline SVG (not a wrapper component) so Menu.ItemIcon's asChild merges its data-c11n-component/css directly onto
// the element — that attribute is what triggers the item's "icon label" grid, keeping the radio beside the label.
const radioMark = (selected: boolean) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="7.25" stroke="currentColor" strokeWidth="1.5" style={{ fill: 'none' }} />
    {selected && <circle cx="9" cy="9" r="4" style={{ fill: 'currentColor' }} />}
  </svg>
);

export function SmartAssignControlCell({
  listingAlias,
  buttonUrl,
  placementSource,
  isSpotlighted,
  packageName: packageNameFromApi,
  spotlightEligible,
  managedByFeed,
  subTitle,
}: {
  listingAlias: string;
  buttonUrl?: string;
  placementSource?: PlacementSource;
  isSpotlighted?: boolean;
  packageName?: string;
  spotlightEligible?: boolean;
  managedByFeed?: boolean;
  subTitle?: string;
}) {
  const ctx = useAutoSpotlight();
  if (!ctx) {
    return null;
  }

  const {
    applyLegacy,
    batchInclude,
    batchUninclude,
    removeLegacy,
    exclude,
    excludedIds,
    isTableLocked,
    managedByFeedIds,
    packageNameById,
    pin,
    pinnedIds,
    revertPinnedToSmartAssign,
    smartConfigMode,
    unexclude,
    vacantIds,
  } = ctx;

  const listingManagedByFeed = managedByFeed ?? managedByFeedIds.has(listingAlias);
  const managedByFeedReadOnly = isManagedByFeedReadOnly({
    buttonUrl,
    managedByFeed: listingManagedByFeed,
  });
  const pinned =
    placementSource !== undefined ? placementSource === PlacementSource.PINNED : pinnedIds.has(listingAlias);
  const auto = placementSource === PlacementSource.AUTO;
  const included = placementSource === PlacementSource.INCLUDED;
  const excludedMarker =
    placementSource !== undefined
      ? placementSource === PlacementSource.EXCLUDED
      : !pinned && excludedIds.has(listingAlias);
  const vacant = spotlightEligible ?? vacantIds.has(listingAlias);
  const smartAssignOn = smartConfigMode !== null;
  // Only actively-placed listings get the Spotlight tag; opted-in/default candidates aren't spotlighted yet, so they
  // show the "Smart Assign" chip alone. An engine-placed AUTO listing reads "Spotlight · Smart Assign".
  // For Smart Assign customers the tag follows placementSource (AUTO/PINNED), which is written synchronously and
  // updated optimistically — so an excluded row drops the tag at once and a promoted row gains it, without waiting
  // on listing-hub's isSpotlighted supplement (which lags a beat and otherwise leaves a stale tag until reload,
  // RECE-2152). Legacy customers have no placementSource, so they keep the supplement's isSpotlighted.
  const shouldDisplaySpotlightTag = smartAssignOn ? auto || pinned : Boolean(isSpotlighted) || pinned;
  const spotlightTagLabel =
    smartAssignOn && auto
      ? `${COPY.product.spotlight} · ${COPY.smartAssignCell.state.smartAssign}`
      : COPY.product.spotlight;
  const packageName = packageNameFromApi ?? packageNameById[listingAlias] ?? COPY.packageNameFallback;

  const productTag = shouldDisplaySpotlightTag ? (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Box css={{ display: 'inline-flex', alignItems: 'center', gap: '100' }}>
        <Icon size="sm" css={{ color: '{colors.icon.accent.brand.hero}', flexShrink: 0 }}>
          <IconSparkleFilled />
        </Icon>
        <Text textStyle="body-sm-bold" css={{ color: '{colors.text.accent.brand.hero}' }}>
          {spotlightTagLabel}
        </Text>
      </Box>
      <Text textStyle="body-xs" css={{ color: '{colors.text.subtle}' }}>
        {packageName}
      </Text>
    </Box>
  ) : (
    <Text textStyle="body-sm" css={{ color: '{colors.text.subtle}' }}>
      {packageName}
    </Text>
  );

  if (spotlightEligible === false) {
    // SFH listings that can't be spotlighted right now carry a spotlight subtitle from the API and get the disabled
    // "Not eligible for Spotlight" chip. MFH and other non-spotlight products (no spotlight subtitle) just show their
    // package name.
    const spotlightIneligible = Boolean(subTitle?.toLowerCase().includes('spotlight'));
    return spotlightIneligible ? (
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
        {productTag}
        <Box css={chipRecipe.raw({ tone: 'disabled' })}>
          <Text textStyle="body-xs-bold" css={{ color: '{colors.text.neutral}', ...chipLabelStyle }}>
            {COPY.state.notEligible}
          </Text>
        </Box>
      </Box>
    ) : (
      <Text textStyle="body-sm" css={{ color: '{colors.text.subtle}' }}>
        {packageName}
      </Text>
    );
  }

  if (managedByFeedReadOnly) {
    return renderManageInFeed(productTag);
  }

  if (!vacant) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
        {productTag}
        <Box css={chipRecipe.raw({ tone: 'disabled' })}>
          <Text textStyle="body-xs-bold" css={{ color: '{colors.text.neutral}', ...chipLabelStyle }}>
            {COPY.state.notEligible}
          </Text>
        </Box>
      </Box>
    );
  }

  const cellCopy = COPY.smartAssignCell;

  const applyPinned = () => pin(listingAlias);
  const applySmartAssign = () => {
    if (smartConfigMode === SmartConfigMode.ALL_EXCLUDED) {
      batchInclude([listingAlias]);
      return;
    }
    if (excludedMarker) {
      unexclude(listingAlias);
      return;
    }
    // RECE-2031: only the Pinned -> Smart Assign revert gets the "hang tight" toast + forced loading view --
    // this is the one action with a multi-second gap and no optimistic count update (RECE-2014).
    if (pinned) {
      void revertPinnedToSmartAssign(listingAlias);
      return;
    }
    batchUninclude([listingAlias]);
  };
  const applyExcluded = () => {
    if (smartConfigMode === SmartConfigMode.ALL_INCLUDED) {
      exclude(listingAlias);
      return;
    }
    batchUninclude([listingAlias]);
  };

  const excludeCopy =
    smartConfigMode === SmartConfigMode.ALL_EXCLUDED ? cellCopy.actions.remove : cellCopy.actions.exclude;
  const optionByTarget: Record<StateKey, Action> = {
    pinned: {
      icon: IconLocationOutline,
      label: cellCopy.actions.pin.label,
      desc: cellCopy.actions.pin.desc,
      onSelect: applyPinned,
    },
    smartAssign: {
      icon: IconSparkleOutline,
      label: cellCopy.actions.smartAssign.label,
      desc: cellCopy.actions.smartAssign.desc,
      onSelect: applySmartAssign,
    },
    excluded: {
      critical: true,
      icon: IconMinusOutline,
      label: excludeCopy.label,
      desc: excludeCopy.desc,
      onSelect: applyExcluded,
    },
  };

  const legacyApply: Action = {
    icon: IconLocationOutline,
    label: cellCopy.legacy.apply.label,
    desc: cellCopy.legacy.apply.desc,
    onSelect: () => applyLegacy(listingAlias),
  };
  const legacyRemove: Action = {
    critical: true,
    icon: IconMinusOutline,
    label: cellCopy.legacy.remove.label,
    desc: cellCopy.legacy.remove.desc,
    // PINNED rows have no promotion for removeLegacy to drop; clear them via batchUninclude (RECE-2031).
    onSelect: pinned ? () => batchUninclude([listingAlias]) : () => removeLegacy(listingAlias),
  };

  if (!smartAssignOn) {
    const spotlighted = Boolean(isSpotlighted) || pinned;
    const stateLabel = spotlighted ? cellCopy.legacy.pinnedLabel : cellCopy.legacy.eligibleLabel;
    const removableManagedByFeedSpotlight = isRemovableManagedByFeedSpotlight({
      buttonUrl,
      managedByFeed: listingManagedByFeed,
    });
    // With Smart Assign off, only the feed can spotlight these listings.
    if (listingManagedByFeed && !removableManagedByFeedSpotlight) {
      return renderManageInFeed(productTag);
    }
    const actions = spotlighted || removableManagedByFeedSpotlight ? [legacyRemove] : [legacyApply];
    return renderCell(
      listingAlias,
      productTag,
      removableManagedByFeedSpotlight ? cellCopy.legacy.pinnedLabel : stateLabel,
      !spotlighted && !removableManagedByFeedSpotlight,
      actions,
      isTableLocked,
    );
  }

  const state: StateKey = pinned
    ? 'pinned'
    : auto || included
      ? 'smartAssign'
      : smartConfigMode === SmartConfigMode.ALL_INCLUDED
        ? excludedMarker
          ? 'excluded'
          : 'smartAssign'
        : 'excluded';

  const stateLabel =
    state === 'excluded' && smartConfigMode === SmartConfigMode.ALL_EXCLUDED
      ? cellCopy.state.removed
      : cellCopy.state[state];
  const availableTargets: Array<StateKey> = listingManagedByFeed
    ? ['smartAssign', 'excluded']
    : ['pinned', 'smartAssign', 'excluded'];
  const actions = availableTargets.map((target) => ({
    ...optionByTarget[target],
    critical: false,
    selected: target === state,
  }));

  return renderCell(listingAlias, productTag, stateLabel, state === 'excluded', actions, isTableLocked);
}

function renderManageInFeed(productTag: JSX.Element) {
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
      {productTag}
      <Box css={chipRecipe.raw({ tone: 'static' })}>
        <Icon size="sm" css={{ color: '{colors.icon.neutral}', flexShrink: 0 }}>
          <IconExternalOutline />
        </Icon>
        <Text textStyle="body-xs-bold" css={{ color: '{colors.text.neutral}', ...chipLabelStyle }}>
          {COPY.state.manageInFeed}
        </Text>
      </Box>
    </Box>
  );
}

function renderCell(
  listingAlias: string,
  productTag: JSX.Element,
  stateLabel: string,
  muted: boolean,
  actions: Array<Action>,
  disabled: boolean,
) {
  // While a spotlight change is in flight the whole table is locked: render the chip as a non-interactive,
  // aria-disabled button (no Menu) so a second pick can't be opened until the current result lands.
  const trigger = (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={`Change spotlight state, currently ${stateLabel}`}
      data-testid={`smart-assign-state-${listingAlias}`}
      onClick={swallow}
      onKeyDown={
        disabled
          ? undefined
          : (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.click();
              }
            }
      }
      css={chipRecipe.raw({ tone: disabled ? 'disabled' : 'interactive' })}
    >
      <Text textStyle="body-xs-bold" css={chipLabelRecipe.raw({ muted })}>
        {stateLabel}
      </Text>
      <Icon size="sm" css={{ color: '{colors.icon.neutral}', flexShrink: 0 }}>
        <IconChevronDownOutline />
      </Icon>
    </Box>
  );

  if (disabled) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
        {productTag}
        {trigger}
      </Box>
    );
  }

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '100' }}>
      {productTag}
      <Menu
        // oxlint-disable-next-line constellation/no-raw-spacing-values
        css={{ minWidth: '240px' }}
        trigger={trigger}
        content={actions.map(({ icon: ActionIcon, label, desc, onSelect, critical, selected }) => {
          // On-mode options render as radios (selected is defined); legacy actions keep their own icon.
          const isRadio = selected !== undefined;
          return (
            <Menu.Item
              key={label}
              css={selected ? selectedOptionRow : undefined}
              onClick={(event) => {
                swallow(event);
                // The selected option is the current state: re-picking it is a no-op (never re-runs its mutation),
                // and it stays highlighted rather than greyed out like a disabled item.
                if (!selected) {
                  onSelect();
                }
              }}
            >
              <Menu.ItemIcon
                render={isRadio ? radioMark(Boolean(selected)) : <ActionIcon />}
                css={menuActionIconRecipe.raw({ critical: Boolean(critical), selected: Boolean(selected) })}
              />
              <Menu.ItemLabel css={menuActionLabelRecipe.raw({ critical: Boolean(critical) })}>{label}</Menu.ItemLabel>
              <Menu.ItemMeta>{desc}</Menu.ItemMeta>
            </Menu.Item>
          );
        })}
      />
    </Box>
  );
}

export default SmartAssignControlCell;
